import { LOCAL_ERROR_FEEDBACK } from '@awork/_shared/services/api-client/local-error-feedback'
import { isGloballySilentHttpError } from '../../functions/is-globally-silent-http-error'
import type { Observable, Subscription } from 'rxjs'
import { catchError, throwError, map, switchMap, tap } from 'rxjs'
import type { OnDestroy } from '@angular/core'
import { inject, Injectable, Injector } from '@angular/core'
import type { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http'
import { HttpContext, HttpContextToken, HttpErrorResponse, HttpHeaders } from '@angular/common/http'
import { ErrorMessage } from '../../classes/error-message'
import { AccountService } from '@awork/_shared/services/account-service/account.service'
import { LogService } from '@awork/_shared/services/log-service/log.service'
import { Level } from '@awork/_shared/services/log-service/types'
import { AppStore } from '@awork/core/state/app.store'
import { AppQuery } from '@awork/core/state/app.query'
import { ToastService } from '../toast-service/toast.service'
import { SocialConnectErrors } from '@awork/_shared/services/account-service/types'
import { CONNECT_ACCESS_RESTRICTION_SAME_DOMAIN } from '@awork/features/project/services/project-service/types'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import { FrontendMetric, MetricsService } from '@awork/_shared/services/metrics-service/metrics.service'
import { SupportDiagnosticsService } from '../support-diagnostics-service/support-diagnostics.service'

interface ExcludedCall {
  url: string
  method: string
  code?: string
  details?: string[]
  description?: string
  validationProperty?: string
}

const EMPTY_RESPONSE = 'empty-response'

// Marks a request that was already sent again after a session refresh, so it is not retried twice
const SESSION_REFRESH_RETRY = new HttpContextToken<boolean>(() => false)

/**
 * Determines whether an error response should be excluded from global error handling.
 * @param {HttpRequest<unknown>} request - Request that failed.
 * @param {HttpErrorResponse | undefined} error - Response to match against local error handling.
 * @returns {boolean} Whether the caller owns feedback for this error.
 */
export function shouldExcludeError(request: HttpRequest<unknown>, error: HttpErrorResponse | undefined): boolean {
  if (request.context.get(LOCAL_ERROR_FEEDBACK)) {
    return true
  }

  // Connector authentication failures are reported by the initiating surface.
  if (
    request.method === 'POST' &&
    /\/agents\/connectors\/[^/?]+\/connections(?:\/[^/?]+)?\/auth\/initiate(?:\?|$)/.test(request.url)
  ) {
    return true
  }

  const excludedCalls: ExcludedCall[] = [
    {
      url: 'intelligence/intent',
      method: 'POST'
    },
    {
      url: 'me/stats',
      method: 'POST'
    },
    {
      url: 'me/supportToken',
      method: 'GET'
    },
    {
      // Planner owns load-error feedback and keeps a Retry action.
      url: 'users/workload/stream',
      method: 'GET'
    },
    {
      url: 'v1/search',
      method: 'GET'
    },
    {
      url: 'me/integrations/calendar/items',
      method: 'GET',
      code: 'no-calendar-integrations-found'
    },
    {
      url: 'me/integrations/calendar/items',
      method: 'GET',
      code: EMPTY_RESPONSE
    },
    {
      url: 'me/integrations',
      method: 'POST'
    },
    {
      url: 'invitations',
      method: 'POST'
    },
    {
      url: 'assignuserbyemail',
      method: 'POST'
    },
    {
      url: 'accounts/refreshcookie',
      method: 'POST'
    },
    {
      url: 'roles',
      method: 'POST',
      code: 'already-exist-error'
    },
    {
      url: 'referrals',
      method: 'POST',
      code: 'email-already-exists'
    },
    {
      url: 'accounts',
      method: 'PUT',
      code: 'account-exists'
    },
    {
      url: 'accounts/forgotPassword',
      method: 'POST',
      code: EMPTY_RESPONSE // Exclude only when the response is empty
    },
    {
      url: 'accounts/forgotPassword',
      method: 'POST',
      code: 'pending-invitation'
    },
    {
      url: 'changepassword',
      method: 'POST',
      code: 'password-set-failed'
    },
    {
      url: 'timetracking/stop',
      method: 'POST',
      code: 'no-active-time-tracking'
    },
    {
      url: 'accounts/connecttosocialaccount',
      method: 'POST',
      code: SocialConnectErrors.NeverLoggedIn
    },
    {
      url: 'teams',
      method: 'POST',
      code: 'validation-failed'
    },
    {
      url: 'projecttimebookings',
      method: 'PUT',
      code: 'validation-failed'
    },
    {
      url: 'projecttimebookings',
      method: 'POST',
      code: 'validation-failed'
    },
    {
      url: 'setrecurrency',
      method: 'POST',
      code: 'validation-failed',
      details: ['subtaskWithRecurringParent', 'parentWithRecurringSubtask']
    },
    {
      url: 'notifications/settings',
      method: 'POST',
      code: 'invalid-operation'
    },
    {
      url: 'roles/moveuser',
      method: 'POST',
      code: 'guest-access-restriction'
    },
    {
      url: 'integrations/calendar/items',
      method: 'GET',
      code: 'no-calendar-integrations-found'
    },
    {
      url: 'integrations',
      method: 'POST',
      code: 'invalid-response-from-server'
    },
    {
      url: 'integrations',
      method: 'POST',
      code: 'user-not-found'
    },
    {
      url: 'clientapplications',
      method: 'POST',
      code: 'duplication-violation'
    },
    {
      url: 'clientapplications',
      method: 'POST',
      code: 'validation-failed'
    },
    {
      url: 'v1/versions',
      method: 'GET'
    },
    {
      url: 'checkcustomsubdomain',
      method: 'POST',
      code: 'duplication-violation'
    },
    {
      url: 'channels',
      method: 'GET',
      code: 'account-disabled-error'
    },
    {
      url: 'accounts/cookie',
      method: 'POST'
    },
    {
      url: 'typeofwork',
      method: 'PUT',
      code: 'duplication-violation'
    },
    {
      url: 'projectstatuses',
      method: 'PUT',
      code: 'duplication-violation'
    },
    {
      url: 'apiusers',
      method: 'POST',
      code: 'duplication-violation'
    },
    {
      url: 'apiusers',
      method: 'PUT',
      code: 'duplication-violation'
    },
    {
      url: 'absences',
      method: 'POST',
      code: 'invalid-operation'
    },
    {
      url: 'tasks/assignuserbyemail',
      method: 'POST',
      code: 'guest-access-restriction'
    },
    {
      url: 'timeentries/',
      method: 'PUT',
      code: 'time-tracking-limit'
    },
    {
      url: 'timeentries/',
      method: 'PUT',
      code: 'invalid-model',
      description: 'Cannot update start date when there are breaks before the new start date'
    },
    {
      url: 'setassignees',
      method: 'POST',
      code: 'invalid-operation'
    },
    {
      url: 'integrations',
      method: 'GET',
      code: 'invalid-user-email'
    },
    {
      url: 'integrations',
      method: 'POST',
      code: 'validation-failed'
    },
    {
      url: 'integrations',
      method: 'POST',
      code: 'token-request-failed'
    },
    {
      url: 'integrations',
      method: 'POST',
      code: 'invalid-domain'
    },
    {
      url: 'integrations',
      method: 'POST',
      code: 'user-profile-request-failed'
    },
    {
      url: 'integrations',
      method: 'POST',
      code: 'domain-request-failed'
    },
    {
      url: 'integrations',
      method: 'POST',
      code: 'unauthorized'
    },
    {
      url: 'integrations',
      method: 'POST',
      code: 'already-configured'
    },
    {
      url: 'integrations',
      method: 'POST',
      code: 'invalid-tenant-id'
    },
    {
      url: '/reauthenticate',
      method: 'POST'
    },
    {
      url: 'integrations',
      method: 'POST',
      code: 'no-sso-enabled'
    },
    {
      url: 'integrations',
      method: 'POST',
      code: 'scim-integration-already-exists'
    },
    {
      url: 'pdf',
      method: 'GET'
    },
    {
      url: 'referralusersettings',
      method: 'GET'
    },
    {
      url: 'projectconnections/validatecode',
      method: 'POST'
    },
    {
      url: 'projectconnections/accept',
      method: 'POST'
    },
    {
      url: 'verifyEmail',
      method: 'POST'
    },
    {
      url: '/absenceregions',
      method: 'POST',
      // TODO: Ask backend to add a more specific error code for duplicate region
      code: 'invalid-operation'
    },
    {
      url: '/absenceregions',
      method: 'PUT',
      // TODO: Ask backend to add a more specific error code for duplicate region
      code: 'invalid-operation'
    },
    {
      url: '/projectconnections',
      method: 'POST',
      code: CONNECT_ACCESS_RESTRICTION_SAME_DOMAIN
    },
    {
      url: '/updatetags',
      method: 'POST',
      code: 'duplication-violation'
    },
    {
      url: 'accounts/ValidateMFAToken',
      method: 'POST',
      code: 'mfa-token-invalid'
    },
    {
      url: 'accounts/mfaconfiguration',
      method: 'POST',
      code: 'mfa-required-by-workspace'
    },
    {
      url: 'projects/validateProjectKey',
      method: 'POST'
    },
    {
      url: 'agents/connectors',
      method: 'POST',
      code: 'validation-failed',
      validationProperty: 'Url'
    },
    {
      // The connector surface shows the MCP server's reason for the failed tool refresh.
      url: 'agents/connectors/',
      method: 'POST',
      code: 'connector-refresh-failed'
    },
    {
      // The report dashboard page shows why its query failed and offers a retry.
      url: 'agents/reports/',
      method: 'POST',
      code: 'validation-failed'
    },
    {
      // The agent thread reloads and shows the approval or question it waits for now.
      url: 'agents/threads/',
      method: 'POST',
      code: 'validation-failed',
      validationProperty: 'AnswerToInterruptId'
    }
  ]

  const checkCodeMatches = (excludedCall: ExcludedCall): boolean =>
    !excludedCall.code ||
    (excludedCall.code === EMPTY_RESPONSE ? !error?.error : !!error?.error && excludedCall.code === error.error.code)

  const checkDetailsMatch = (excludedCall: ExcludedCall): boolean =>
    !excludedCall.details ||
    (!!error?.error &&
      Array.isArray(excludedCall.details) &&
      Array.isArray(error.error.details) &&
      excludedCall.details.some(excludedDetail => error.error.details.includes(excludedDetail)))

  const checkDescriptionMatches = (excludedCall: ExcludedCall): boolean =>
    !excludedCall.description ||
    (!!error?.error &&
      typeof excludedCall.description === 'string' &&
      typeof error.error.description === 'string' &&
      excludedCall.description === error.error.description)

  /**
   * Checks whether an error matches the excluded validation property.
   * @param {ExcludedCall} excludedCall
   * @returns {boolean}
   */
  function checkValidationPropertyMatches(excludedCall: ExcludedCall): boolean {
    if (!excludedCall.validationProperty) {
      return true
    }

    if (!error?.error || !Array.isArray(error.error.validationErrors)) {
      return false
    }

    for (const validationError of error.error.validationErrors) {
      if (validationError?.property === excludedCall.validationProperty) {
        return true
      }
    }

    return false
  }

  return excludedCalls.some(excludedCall => {
    const urlMatches = request.url.includes(excludedCall.url)
    const methodMatches = request.method === excludedCall.method
    const codeMatches = checkCodeMatches(excludedCall)
    const detailsMatch = checkDetailsMatch(excludedCall)
    const descriptionMatches = checkDescriptionMatches(excludedCall)
    const validationPropertyMatches = checkValidationPropertyMatches(excludedCall)

    return urlMatches && methodMatches && codeMatches && detailsMatch && descriptionMatches && validationPropertyMatches
  })
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor, OnDestroy {
  private injector = inject(Injector)
  private apiClient = inject(ApiClient)
  private metricsService = inject(MetricsService)
  private supportDiagnosticsService = inject(SupportDiagnosticsService)

  private error: HttpErrorResponse
  private logService: LogService
  private toastService: ToastService
  private accountService: AccountService
  private appQuery: AppQuery
  private appStore: AppStore
  private request: HttpRequest<unknown>
  private mySubscriptions: Subscription[] = []
  private apiBlockedCounter = 0
  private apiBlockedTimeout: number

  constructor() {
    this.interceptClientErrors()
  }

  /**
   * Intercepts HTTP requests and handles API errors.
   * @param {HttpRequest<unknown>} req
   * @param {HttpHandler} next
   * @returns {Observable<HttpEvent<unknown>>}
   */
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const requestStartedAtMs = Date.now()

    try {
      this.logService = this.injector.get(LogService)
      this.toastService = this.injector.get(ToastService)
      this.accountService = this.injector.get(AccountService)
      this.appQuery = this.injector.get(AppQuery)
      this.appStore = this.injector.get(AppStore)
    } catch (_) {}

    return next.handle(req).pipe(
      tap(
        (event: HttpEvent<unknown>) => {},
        error => {
          if (error instanceof HttpErrorResponse) {
            this.error = error
            this.trackFailedRequest(req, error, requestStartedAtMs)

            if (error.status === 0 && error.statusText === 'Unknown Error') {
              // After three blocked api calls, reload the page
              this.apiBlockedCounter++

              if (this.apiBlockedCounter >= 3) {
                window.location.reload()
              }

              // Reset the counter after 5 seconds, in the case browser resumed connection normally
              if (!this.apiBlockedTimeout) {
                this.apiBlockedTimeout = window.setTimeout(() => {
                  this.apiBlockedCounter = 0
                  this.apiBlockedTimeout = undefined
                }, 5000)
              }

              return void 0
            }

            this.logTrace(error, req)

            if (isGloballySilentHttpError(error)) {
              if (error.status === 401 && !this.canRetryAfterSessionRefresh(req, error)) {
                const message = error.error?.exp ?? ''

                this.handleUnauthorized(message)
              }

              return void 0
            }

            switch (error.status) {
              case 400:
                this.handleBadRequest(req)
                break
              case 500:
                this.handleServerError(req)
                break
              case 503:
                this.handleTimeout(req)
                break
              default:
                this.showError('Sorry, the request failed. Please try again in a moment', req)
            }
          }

          return throwError(error)
        }
      ),
      map((event: HttpEvent<unknown>) => {
        this.logTrace(event as HttpResponse<unknown>, req)
        return event
      }),
      catchError((error: unknown) => {
        if (!this.canRetryAfterSessionRefresh(req, error)) {
          return throwError(() => error)
        }

        // An expired access token fails the request. Send it once more with the refreshed session
        // instead of dropping it and reloading the page, which loses actions such as a timer start.
        const context = new HttpContext()

        for (const token of req.context.keys()) {
          context.set(token, req.context.get(token))
        }

        const retryRequest = req.clone({ context: context.set(SESSION_REFRESH_RETRY, true) })

        return this.accountService.refreshSession(false).pipe(switchMap(() => this.intercept(retryRequest, next)))
      })
    )
  }

  /**
   * Checks whether a request failed because of an expired access token and was not sent again yet.
   * Kong answers an expired token with a 401 without an API error code.
   * @param {HttpRequest<unknown>} request
   * @param {unknown} error
   * @returns {boolean}
   */
  private canRetryAfterSessionRefresh(request: HttpRequest<unknown>, error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      error.status === 401 &&
      !(error.error?.code || error.error?.Code) &&
      !request.context.get(SESSION_REFRESH_RETRY) &&
      !request.url.includes('/accounts/') &&
      !!this.accountService &&
      !!this.appQuery?.getIsLoggedIn()
    )
  }

  /**
   * Stores failed request diagnostics for support exports.
   * @param {HttpRequest<unknown>} request
   * @param {HttpErrorResponse} error
   * @param {number} startedAtMs
   */
  private trackFailedRequest(request: HttpRequest<unknown>, error: HttpErrorResponse, startedAtMs: number): void {
    this.supportDiagnosticsService.addFailedRequest({
      timestamp: new Date().toISOString(),
      method: request.method,
      url: this.getSanitizedRequestUrl(request.urlWithParams),
      status: error.status,
      traceId: error.headers?.get('trace-id') ?? null,
      durationMs: Math.max(0, Date.now() - startedAtMs)
    })
  }

  /**
   * Removes query parameters from a request URL to avoid persisting sensitive values.
   * @param {string} rawUrl
   * @returns {string}
   */
  private getSanitizedRequestUrl(rawUrl: string): string {
    try {
      const parsedUrl = new URL(rawUrl, window.location.origin)

      return `${parsedUrl.origin}${parsedUrl.pathname}`
    } catch {
      return rawUrl.split('?')[0]
    }
  }

  /**
   * Handles the bad request error
   * @param {HttpRequest<unknown>} request
   */
  private handleBadRequest(request: HttpRequest<unknown>): void {
    if (this.error.headers?.get('trace-id') && this.error.error) {
      this.showError(null, request)
    } else if (!this.appQuery.getAdBlockerErrorOccurred()) {
      // Show the AdBlocker/AdGuard API error just once
      this.appStore.setAdBlockerErrorOccurred(true)
      this.showError(q.translations.errors.AdBlockerError, request)
    }

    if (!this.excludeErrors(request)) {
      this.logService?.logMessage(Level.error, `Bad request: ${this.error.url} - Code: ${this.error.error?.code}`, {
        error: this.error
      })
    }
  }

  /**
   * Handles unauthorized errors and attempts a token refresh when needed.
   * @param {string} message
   */
  private handleUnauthorized(message: string): void {
    // If the error response is not a valid API error response
    // the error comes from KONG, therefore, it is likely that the reason is token related
    if (
      (!this.error.error || !(this.error.error.code || this.error.error.Code)) &&
      this.appQuery?.getIsLoggedIn() &&
      this.accountService
    ) {
      this.accountService.refreshSession?.().subscribe()
    }
  }

  /**
   * Handle server errors
   * @param {HttpRequest<unknown>} request
   */
  private handleServerError(request: HttpRequest<unknown>): void {
    this.error.error !== null
      ? this.showError(null, request)
      : this.showError(q.translations.Error.serverError, request)
  }

  /**
   * Handle Timeout
   * @param {HttpRequest<unknown>} request
   */
  private handleTimeout(request: HttpRequest<unknown>): void {
    this.error.error !== null
      ? this.showError(null, request)
      : this.showError(q.translations.Error.serverUnavailable, request)
  }

  /**
   * Shows the error using the toast component
   * @param {string} message
   * @param {HttpRequest<unknown>} request
   */
  private showError(message: string, request: HttpRequest<unknown>): void {
    if (!this.excludeErrors(request) && this.toastService) {
      if (this.error?.status === 400) {
        this.metricsService.incrementMetric(FrontendMetric.BadRequestToastShown, 1).subscribe()
      }

      if (!message) {
        const errorMessage = ErrorMessage.getErrorMessage(this.error)

        this.toastService.show(errorMessage.description, { title: errorMessage.title, type: 'error' })
      } else {
        this.toastService.show(message, { type: 'error' })
      }
    }
  }

  /**
   * Determines if the error should be shown according to some endpoints excluded
   * @param {HttpRequest<unknown>} request
   * @returns {boolean}
   */
  private excludeErrors(request: HttpRequest<unknown>): boolean {
    return shouldExcludeError(request, this.error)
  }

  /**
   * Cleans up subscriptions on destroy.
   */
  ngOnDestroy() {
    this.mySubscriptions.map(s => s.unsubscribe())
  }

  /**
   * Logs the trace id of the response
   * @param {HttpResponse<unknown> | HttpErrorResponse} event
   * @param {HttpRequest<unknown>} request
   */
  private logTrace(event: HttpResponse<unknown> | HttpErrorResponse, request: HttpRequest<unknown>): void {
    if (event.headers && event.headers instanceof HttpHeaders && request && request.method !== 'GET') {
      this.logService?.logTrace(event.headers.get('trace-id'), event instanceof HttpErrorResponse)
    }
  }

  /**
   * Intercepts errors from the APIClient
   */
  private interceptClientErrors(): void {
    this.apiClient.clientError$.subscribe(errorMessage => {
      this.toastService.show(errorMessage || q.translations.errors.somethingWentWrong, { type: 'error' })
    })
  }
}
